import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Grid, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField, FormGroup, FormControlLabel, Checkbox, FormHelperText } from '@mui/material';
import { getDisplayStoreNumber } from '../../../utils/storeNumber';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { useState, useEffect, SyntheticEvent } from 'react';
import { AddODSModel, Instance, Market, MarketOrderType, MarketSupplier, client } from '../../../app/services/api/orderManagementClient';
import { Store } from '../../../app/services/api/generated';
import { isAutoShipOrder, isOrderTypeDSD } from '../../../enums/OrderType';
export interface AddODSModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (odsData: AddODSModel) => Promise<void>;
  title?: string;
  instances: Instance[];
}

function AddODSModal(props: AddODSModalProps) {
  const { t } = useTranslation('odsmanagement');
  
  const [instanceId, setInstanceId] = useState<number | null>(null);
  const [marketId, setMarketId] = useState<number | null>(null);
  const [orderTypeId, setOrderTypeId] = useState<number | null>(null);
  const [supplier, setSupplier] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<MarketSupplier[]>([]); 
  const [cutOffDate, setCutOffDate] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [selectAllStores, setSelectAllStores] = useState<boolean>(false);

  const [markets, setMarkets] = useState<Market[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [stores, setStores] = useState<Store[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateValidationError, setDateValidationError] = useState<string>('');

  useEffect(() => {
    if (isOrderTypeDSD(orderTypeId ?? -1) && instanceId && marketId) {
      loadSuppliers();
      setSupplier(null);
    } else {
      setSuppliers([]);
      setSupplier(null);
    }
  }, [orderTypeId, instanceId, marketId]);

  useEffect(() => {
    if (instanceId) {
      loadMarkets(instanceId);
      setMarketId(null);
      setOrderTypeId(null);
      setSelectedStores([]);
      setSelectAllStores(false);
    } else {
      setMarkets([]);
      setMarketId(null);
    }
  }, [instanceId]);

  useEffect(() => {
    if (instanceId && marketId) {
      loadOrderTypes();
      loadStores();
      setOrderTypeId(null);
      setSelectedStores([]);
      setSelectAllStores(false);
    } else {
      setOrderTypes([]);
      setStores([]);
    }
  }, [marketId, instanceId]);

  const loadMarkets = async (instanceId: number) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId?.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load markets:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const filterOptions = {
        companyId: instanceId?.toString(),
        customerTypeId: marketId?.toString(),
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      };

      const response = await client.getMarketSuppliers(filterOptions);
      
      if (response.success && response.data) {
        setSuppliers(response.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadOrderTypes = async () => {
    try {
      const options = {
        companyId: instanceId?.toString(),
        customerTypeId: marketId?.toString(),
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 999999,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getMarketOrderTypes(options);

      if (result.success && result.data) {
        const nonAutoshipOrderTypes = result.data.items.filter(x => !isAutoShipOrder(x.orderTypeId));
        setOrderTypes(nonAutoshipOrderTypes);
      } else {
        setOrderTypes([]);
      }
    } catch (error) {
      console.error('Error fetching order types:', error);
      setOrderTypes([]);
    }
  };

  const loadStores = async () => {
    try {
      const filterOptions = {
        companyId: instanceId?.toString(),
        customerTypeId: marketId?.toString(),
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      };

      const response = await client.getMarketStores(filterOptions);
      
      if (response.success && response.data) {
        setStores(response.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (props.open) {
      setInstanceId(null);
      setMarketId(null);
      setOrderTypeId(null);
      setSupplier(null);
      setCutOffDate('');
      setDeliveryDate('');
      setSelectedStores([]);
      setSelectAllStores(false);
      setMarkets([]);
      setOrderTypes([]);
      setStores([]);
      setSuppliers([]);
    }
  }, [props.open]);

  const handleInstanceChange = (newInstanceId: number) => {
    setInstanceId(newInstanceId);
  };

  const handleMarketChange = (newMarketId: number) => {
    setMarketId(newMarketId);
  };

  const handleClose = () => {
    // Reset all form state
    setInstanceId(null);
    setMarketId(null);
    setOrderTypeId(null);
    setSupplier(null);
    setCutOffDate('');
    setDeliveryDate('');
    setSelectedStores([]);
    setSelectAllStores(false);
    setMarkets([]);
    setOrderTypes([]);
    setStores([]);
    setSuppliers([]);
    props.onClose();
  };

  const handleConfirm = async () => {
    if (!instanceId || !marketId || orderTypeId == null || !cutOffDate || !deliveryDate) {
      return;
    }

    if (isOrderTypeDSD(orderTypeId ?? -1) && !supplier) {
      return;
    }

    const storeNumbers = (selectAllStores 
      ? stores.map(store => store.storeNumber)
      : selectedStores.map(store => store.storeNumber)) as string[];

    if (storeNumbers.length === 0) {
      return;
    }

    const odsData: AddODSModel = {
      instanceId,
      marketId,
      orderTypeId,
      supplier: isOrderTypeDSD(orderTypeId ?? -1) ? supplier : null,
      cutOffDate,
      deliveryDate,
      storeNumbers,
      languageCode: localStorage.getItem('i18nextLng') ?? 'en-GB',
    };

    setIsLoading(true);
    try {
      await props.onConfirm(odsData);
      handleClose();
    } catch (error) {
      console.error('Error creating ODS:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAllStoresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores(checked ? stores : []);
    setSelectAllStores(checked);
  };

  const isFormValid = () => {
    const baseValid = instanceId && marketId && orderTypeId != null && cutOffDate && deliveryDate && 
            (selectAllStores || selectedStores.length > 0);
    
    const datesValid = cutOffDate && deliveryDate ? 
      new Date(cutOffDate) < new Date(deliveryDate) : true;
    
    if (isOrderTypeDSD(orderTypeId ?? -1)) {
      return baseValid && supplier != null && datesValid;
    }
    
    return baseValid && datesValid;
  };

  const validateDates = () => {
    if (cutOffDate && deliveryDate) {
      const cutOffDateTime = new Date(cutOffDate);
      const deliveryDateTime = new Date(deliveryDate);
      
      if (cutOffDateTime >= deliveryDateTime) {
        setDateValidationError(t('cutOffDateMustBeBeforeDeliveryDate'));
        return false;
      }
    }
    setDateValidationError('');
    return true;
  };

  useEffect(() => {
    if (cutOffDate && deliveryDate) {
      validateDates();
    }
  }, [cutOffDate, deliveryDate]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      aria-labelledby="create-ods-dialog"
      open={props.open}
    >
      <DialogTitle>
        {props.title || t('createNewODS')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <Grid container 
          spacing={5}>
          <Grid item 
            xs={12} 
            md={6}>
            <FormControl fullWidth>
              <InputLabel shrink>{t('instance')}</InputLabel>
              <Select
                value={instanceId || ''}
                onChange={(e) => handleInstanceChange(Number(e.target.value))}
                label={t('instance')}
                displayEmpty
                notched
              >
                {props.instances.map((instance) => (
                  <MenuItem key={instance.id} 
                    value={instance.id}>
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item 
            xs={12} 
            md={6}>
            <FormControl fullWidth>
              <InputLabel shrink>{t('market')}</InputLabel>
              <Select
                value={marketId || ''}
                onChange={(e) => handleMarketChange(Number(e.target.value))}
                label={t('market')}
                displayEmpty
                notched
                disabled={!instanceId}
              >
                {markets.map((market) => (
                  <MenuItem key={market.customerTypeId} 
                    value={market.customerTypeId}>
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item 
            xs={12} 
            md={6}>
            <FormControl fullWidth>
              <InputLabel shrink>{t('orderType')}</InputLabel>
              <Select
                value={orderTypeId}
                onChange={(e) => setOrderTypeId(Number(e.target.value))}
                label={t('orderType')}
                notched
                disabled={!marketId}
              >
                {orderTypes.map((orderType) => (
                  <MenuItem key={orderType.orderTypeId} 
                    value={orderType.orderTypeId}>
                    {orderType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isOrderTypeDSD(orderTypeId ?? -1) && (
            <Grid item 
              xs={12} 
              md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>{t('supplier')}</InputLabel>
                <Select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  label={t('supplier')}
                  displayEmpty
                  notched
                  disabled={!marketId}
                >
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.supplierId} 
                      value={supplier.supplierNumber}>
                      {supplier.supplierName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {!isOrderTypeDSD(orderTypeId ?? -1) && (
            <Grid item 
              xs={12} 
              md={6}>
            </Grid>
          )}

          <Grid item 
            xs={12} 
            md={6}>
            <DatePickerLocalized
              useCalendar={false}
              label={t('cutOffDate')}
              value={cutOffDate}
              shouldDisableDate={(date) => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                return  d < tomorrow;
              }}
              format="DD/MM/YYYY"
              translations={{
                clearButtonLabel: t('calendar.clearButtonLabel'),
                todayButtonLabel: t('calendar.todayButtonLabel'),
                cancelButtonLabel: t('calendar.cancelButtonLabel'),
                okButtonLabel: t('calendar.okButtonLabel'),
              }}
              onChange={(newValue: string | null) => {
                setCutOffDate(newValue ?? '');
              }}
            />
            {dateValidationError && (
              <FormHelperText error 
                sx={{ mt: 1 }}>
                {dateValidationError}
              </FormHelperText>
            )}
          </Grid>

          <Grid item 
            xs={12}
            md={6}>
            <DatePickerLocalized
              useCalendar={false}
              format="DD/MM/YYYY"
              label={t('deliveryDate')}
              value={deliveryDate}
              shouldDisableDate={(date) => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                return  d < tomorrow;
              }}
              translations={{
                clearButtonLabel: t('calendar.clearButtonLabel'),
                todayButtonLabel: t('calendar.todayButtonLabel'),
                cancelButtonLabel: t('calendar.cancelButtonLabel'),
                okButtonLabel: t('calendar.okButtonLabel'),
              }}
              onChange={(newValue: string | null) => {
                setDeliveryDate(newValue ?? '');
              }}
            />
          </Grid>

          <Grid item 
            xs={12} 
            md={10}>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                multiple
                limitTags={3}
                options={stores ?? []}
                value={selectedStores}
                disabled={selectAllStores || !marketId}
                onChange={(event: SyntheticEvent, value: Store[]) => {
                  setSelectedStores(value);
                  setSelectAllStores(false);
                }}
                getOptionKey={(option) => option.storeNumber ?? ''}
                getOptionLabel={(option) => `${getDisplayStoreNumber(option)} - ${option.storeName}`}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('selectStores')}
                    placeholder={t('selectStores')} />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item 
            xs={12} 
            md={2}>
            <FormGroup style={{ textAlign: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllStores}
                    onChange={handleSelectAllStoresChange}
                    disabled={!marketId}
                  />
                }
                label={t('selectAll')}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button 
          variant="secondary"
          onClick={handleClose}
          size="lg">
          {t('cancel')}
        </Button>
        <Button 
          variant="primary"
          onClick={handleConfirm}
          size="lg"
          disabled={!isFormValid() || isLoading}>
          {isLoading ? t('creating') : t('create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddODSModal;